/**
 * Compliance Issues Log — CMS Pre-Audit Issue Summary alignment.
 * Owner view: root cause, remediation, updates (no status/closure).
 * Compliance view: full form, controls status and closure.
 */
import React, { useState, useEffect, useCallback } from 'react';
import HubCorrespondence from '../Calendar/HubCorrespondence';
import PhiFriendlyNote from '../../components/PhiFriendlyNote.jsx';
import ClosedIssues from './ClosedIssues';
import { statusLabel, sourceLabel, isClosed } from './cilLabels';

const API = process.env.REACT_APP_API_URL || '';
const CIL = `${API}/api/compliance-issues-log`;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const CMS_DATE_RE = /^\d{4}\/\d{2}\/\d{2}$/;
function pad2(n) { return String(n).padStart(2, '0'); }
function isoToCmsDate(iso) {
  if (!iso || typeof iso !== 'string') return '';
  const s = String(iso).trim();
  if (CMS_DATE_RE.test(s)) return s;
  if (ISO_DATE_RE.test(s)) return s.replaceAll('-', '/');
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
}
function cmsToIsoDate(cms) {
  if (!cms || typeof cms !== 'string') return null;
  const s = String(cms).trim();
  if (ISO_DATE_RE.test(s)) return s;
  if (!CMS_DATE_RE.test(s)) return null;
  return s.replaceAll('/', '-');
}

const STATUS_OPTIONS = ['open', 'in_progress', 'closed'];
// The Open issues tab never lists closed issues; those live on their own tab.
const OPEN_STATUS_FILTERS = ['open', 'in_progress'];
const SOURCE_OPTIONS = ['internal_audit', 'monitoring', 'self_identified', 'disclosed_to_cms', 'other'];

function TabBar({ value, onChange, tabs }) {
  return (
    <div className="inline-flex rounded-xl bg-slate-100 p-1 gap-0.5 mr-3">
      {tabs.map(([v, l, count]) => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 ${value === v ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
          {l}
          {typeof count === 'number' && (
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${value === v ? 'bg-slate-100 text-slate-700' : 'bg-slate-200/70 text-slate-500'}`}>{count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

const defaultForm = {
  department_ids: [],
  accountable_owner_ids: [],
  program_area_impacted: '',
  issue_description: '',
  root_cause_analysis: '',
  member_impact: '',
  enrollees_impacted: '',
  date_issue_identified: '',
  date_issue_disclosed_to_cms: '',
  disclosed_to_name: '',
  fully_remediated_flag: '',
  system_remediation_description: '',
  system_remediation_started_at: '',
  system_remediation_completed_at: '',
  status: 'open',
  source: '',
  entity_kind: 'internal',
  fdr_entity_id: '',
  fdr_contact_email: ''
};

const ownerFormDefaults = {
  root_cause_analysis: '',
  enrollees_impacted: '',
  system_remediation_description: '',
  system_remediation_started_at: '',
  system_remediation_completed_at: ''
};

export default function ComplianceIssuesLog({ user }) {
  const [issues, setIssues] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [usersByDept, setUsersByDept] = useState([]);
  const [fdrEntities, setFdrEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // The issue's status as last saved (captured when the edit form opens). The
  // content lock keys off THIS, not the live dropdown value, so selecting
  // "closed" in the form does not instantly lock it before you can save.
  const [editingStatus, setEditingStatus] = useState('open');
  const [form, setForm] = useState(defaultForm);
  const [ownerForm, setOwnerForm] = useState(ownerFormDefaults);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cilView, setCilView] = useState('log');
  const [notice, setNotice] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [ownerUpdates, setOwnerUpdates] = useState([]);
  const [newUpdateText, setNewUpdateText] = useState('');
  const [addingUpdate, setAddingUpdate] = useState(false);
  const [allowedCilDepartmentIds, setAllowedCilDepartmentIds] = useState([]);
  const [isComplianceFromApi, setIsComplianceFromApi] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addModalForm, setAddModalForm] = useState({
    department_ids: [],
    program_area_impacted: '',
    issue_description: '',
    root_cause_analysis: '',
    member_impact: '',
    enrollees_impacted: '',
    date_issue_identified: '',
    system_remediation_description: '',
    system_remediation_started_at: '',
    system_remediation_completed_at: '',
    source: ''
  });
  const showLimitedView = isComplianceFromApi === false;
  const isDepartmentBound = allowedCilDepartmentIds.length > 0;

  // The whole log loads once; the Open issues filters and the Closed issues
  // refinements apply on screen, so the tab counts always describe the full log.
  const loadIssues = useCallback(() => {
    setLoading(true);
    fetch(CIL, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setIssues(d.issues || []);
          setAllowedCilDepartmentIds(Array.isArray(d.allowedCilDepartmentIds) ? d.allowedCilDepartmentIds : []);
          setIsComplianceFromApi(Boolean(d.isComplianceUser));
        }
        setLoading(false);
      })
      .catch(() => { setIssues([]); setLoading(false); setIsComplianceFromApi((prev) => (prev === null ? false : prev)); });
  }, []);

  const loadDepartments = useCallback(() => {
    fetch(`${API}/api/departments`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setDepartments(Array.isArray(d) ? d : []))
      .catch(() => setDepartments([]));
  }, []);

  const loadFdrEntities = useCallback(() => {
    fetch(`${CIL}/fdr-entities`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setFdrEntities(d.success && Array.isArray(d.entities) ? d.entities : []))
      .catch(() => setFdrEntities([]));
  }, []);

  useEffect(() => { loadIssues(); }, [loadIssues]);
  useEffect(() => { loadDepartments(); }, []);
  useEffect(() => { loadFdrEntities(); }, [loadFdrEntities]);

  useEffect(() => {
    const ids = Array.isArray(form.department_ids) ? form.department_ids.filter(Boolean) : [];
    if (ids.length === 0) {
      setUsersByDept([]);
      setForm((f) => ({ ...f, accountable_owner_ids: [] }));
      return;
    }
    fetch(`${CIL}/users-by-departments?ids=${ids.join(',')}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setUsersByDept(d.success ? d.users || [] : []))
      .catch(() => setUsersByDept([]));
  }, [form.department_ids]);

  const loadOwnerUpdates = useCallback((issueId) => {
    if (!issueId) return;
    fetch(`${CIL}/${issueId}/owner-updates`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setOwnerUpdates(d.success ? d.updates || [] : []))
      .catch(() => setOwnerUpdates([]));
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(defaultForm);
    setFormOpen(true);
    setError('');
  };

  const openEdit = (issue) => {
    setEditingId(issue.id);
    setEditingStatus(issue.status || 'open');
    const deptIds = Array.isArray(issue.department_ids) ? issue.department_ids.filter(Boolean) : (issue.department_id ? [issue.department_id] : []);
    const ownerIds = Array.isArray(issue.accountable_owner_ids) ? issue.accountable_owner_ids.filter(Boolean) : (issue.accountable_owner_id ? [issue.accountable_owner_id] : []);
    setForm({
      department_ids: deptIds,
      accountable_owner_ids: ownerIds,
      program_area_impacted: issue.program_area_impacted || '',
      issue_description: issue.issue_description || '',
      root_cause_analysis: issue.root_cause_analysis || '',
      member_impact: issue.member_impact || '',
      enrollees_impacted: issue.enrollees_impacted ?? '',
      date_issue_identified: isoToCmsDate(issue.date_issue_identified),
      date_issue_disclosed_to_cms: isoToCmsDate(issue.date_issue_disclosed_to_cms),
      disclosed_to_name: issue.disclosed_to_name || '',
      fully_remediated_flag: issue.fully_remediated_flag || '',
      system_remediation_description: issue.system_remediation_description || '',
      system_remediation_started_at: isoToCmsDate(issue.system_remediation_started_at),
      system_remediation_completed_at: isoToCmsDate(issue.system_remediation_completed_at),
      status: issue.status || 'open',
      source: issue.source || '',
      entity_kind: issue.entity_kind || (issue.fdr_entity_id ? 'fdr' : 'internal'),
      fdr_entity_id: issue.fdr_entity_id || '',
      fdr_contact_email: issue.fdr_contact_email || ''
    });
    setOwnerForm({
      root_cause_analysis: issue.root_cause_analysis || '',
      enrollees_impacted: issue.enrollees_impacted ?? '',
      system_remediation_description: issue.system_remediation_description || '',
      system_remediation_started_at: isoToCmsDate(issue.system_remediation_started_at),
      system_remediation_completed_at: isoToCmsDate(issue.system_remediation_completed_at)
    });
    setOwnerUpdates(issue.owner_updates || []);
    setNewUpdateText('');
    setFormOpen(true);
    setError('');
  };

  const openOwnerEdit = (issue) => {
    setEditingId(issue.id);
    setFormOpen(true);
    setError('');
    setNewUpdateText('');
    fetch(`${CIL}/${issue.id}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.issue) {
          const i = d.issue;
          setOwnerForm({
            root_cause_analysis: i.root_cause_analysis || '',
            enrollees_impacted: i.enrollees_impacted ?? '',
            system_remediation_description: i.system_remediation_description || '',
            system_remediation_started_at: isoToCmsDate(i.system_remediation_started_at),
            system_remediation_completed_at: isoToCmsDate(i.system_remediation_completed_at)
          });
          setOwnerUpdates(i.owner_updates || []);
        }
      })
      .catch(() => setError('Failed to load issue'));
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingId(null);
    setEditingStatus('open');
    loadIssues();
  };

  const openAddModal = () => {
    setAddModalForm({
      department_ids: allowedCilDepartmentIds.slice(),
      program_area_impacted: '',
      issue_description: '',
      root_cause_analysis: '',
      member_impact: '',
      enrollees_impacted: '',
      date_issue_identified: '',
      system_remediation_description: '',
      system_remediation_started_at: '',
      system_remediation_completed_at: '',
      source: ''
    });
    setError('');
    setAddModalOpen(true);
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    loadIssues();
  };

  const handleAddModalSubmit = (e) => {
    e.preventDefault();
    setError('');
    const deptIds = (addModalForm.department_ids && addModalForm.department_ids.length) ? addModalForm.department_ids : allowedCilDepartmentIds;
    if (!deptIds.length) {
      setError('No department selected.');
      return;
    }
    setSaving(true);
    const payload = {
      department_ids: deptIds,
      program_area_impacted: addModalForm.program_area_impacted || null,
      issue_description: addModalForm.issue_description || null,
      root_cause_analysis: addModalForm.root_cause_analysis || null,
      member_impact: addModalForm.member_impact || null,
      enrollees_impacted: addModalForm.enrollees_impacted === '' ? null : (parseInt(addModalForm.enrollees_impacted, 10) || null),
      date_issue_identified: addModalForm.date_issue_identified ? cmsToIsoDate(addModalForm.date_issue_identified) : null,
      system_remediation_description: addModalForm.system_remediation_description || null,
      system_remediation_started_at: addModalForm.system_remediation_started_at ? cmsToIsoDate(addModalForm.system_remediation_started_at) : null,
      system_remediation_completed_at: addModalForm.system_remediation_completed_at ? cmsToIsoDate(addModalForm.system_remediation_completed_at) : null,
      source: addModalForm.source || null,
      status: 'open'
    };
    fetch(CIL, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) closeAddModal();
        else setError(d.message || 'Create failed');
        setSaving(false);
      })
      .catch(() => { setError('Request failed'); setSaving(false); });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    const deptIds = Array.isArray(form.department_ids) ? form.department_ids.filter(Boolean) : [];
    if (!editingId && deptIds.length === 0) {
      setError('Select at least one department.');
      return;
    }
    const isFdr = form.entity_kind === 'fdr';
    if (isFdr && !form.fdr_entity_id) {
      setError('Select the FDR this issue pertains to.');
      return;
    }
    const payload = {
      ...form,
      department_ids: Array.isArray(form.department_ids) ? form.department_ids.filter(Boolean) : [],
      accountable_owner_ids: Array.isArray(form.accountable_owner_ids) ? form.accountable_owner_ids.filter(Boolean) : [],
      enrollees_impacted: form.enrollees_impacted === '' ? null : (parseInt(form.enrollees_impacted, 10) || null),
      date_issue_identified: form.date_issue_identified ? cmsToIsoDate(form.date_issue_identified) : null,
      date_issue_disclosed_to_cms: form.date_issue_disclosed_to_cms ? cmsToIsoDate(form.date_issue_disclosed_to_cms) : null,
      system_remediation_started_at: form.system_remediation_started_at ? cmsToIsoDate(form.system_remediation_started_at) : null,
      system_remediation_completed_at: form.system_remediation_completed_at ? cmsToIsoDate(form.system_remediation_completed_at) : null,
      entity_kind: isFdr ? 'fdr' : 'internal',
      fdr_entity_id: isFdr ? (form.fdr_entity_id || null) : null,
      fdr_contact_email: isFdr ? (form.fdr_contact_email || null) : null
    };
    if (editingId) {
      setSaving(true);
      fetch(`${CIL}/${editingId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then((r) => r.json())
        .then((d) => {
          if (d.success) {
            if (payload.status === 'closed' && editingStatus !== 'closed') {
              setNotice(`${form.program_area_impacted || 'The issue'} is closed. Its record now lives under Closed issues.`);
            }
            closeForm();
          } else setError(d.message || 'Update failed');
          setSaving(false);
        })
        .catch(() => { setError('Request failed'); setSaving(false); });
    } else {
      setSaving(true);
      fetch(CIL, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then((r) => r.json())
        .then((d) => { if (d.success) closeForm(); else setError(d.message || 'Create failed'); setSaving(false); })
        .catch(() => { setError('Request failed'); setSaving(false); });
    }
  };

  const handleOwnerSubmit = (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      root_cause_analysis: ownerForm.root_cause_analysis || null,
      enrollees_impacted: ownerForm.enrollees_impacted === '' ? null : (parseInt(ownerForm.enrollees_impacted, 10) || null),
      system_remediation_description: ownerForm.system_remediation_description || null,
      system_remediation_started_at: ownerForm.system_remediation_started_at ? cmsToIsoDate(ownerForm.system_remediation_started_at) : null,
      system_remediation_completed_at: ownerForm.system_remediation_completed_at ? cmsToIsoDate(ownerForm.system_remediation_completed_at) : null
    };
    setSaving(true);
    fetch(`${CIL}/${editingId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then((r) => r.json())
      .then((d) => { if (d.success) { setForm((f) => ({ ...f, ...payload })); setError(''); } else setError(d.message || 'Update failed'); setSaving(false); })
      .catch(() => { setError('Request failed'); setSaving(false); });
  };

  // Reopening happens on the Closed issues tab, with a reason that stays on the record.
  const openIssues = issues.filter((i) => !isClosed(i)
    && (!filterStatus || i.status === filterStatus)
    && (!filterDepartment || (Array.isArray(i.department_ids) ? i.department_ids.map(String).includes(String(filterDepartment)) : String(i.department_id) === String(filterDepartment))));
  const closedIssues = issues.filter(isClosed);

  const handleAddOwnerUpdate = () => {
    if (!newUpdateText.trim() || !editingId) return;
    setAddingUpdate(true);
    fetch(`${CIL}/${editingId}/owner-updates`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ update_text: newUpdateText.trim() })
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setOwnerUpdates((prev) => [...prev, { ...d.update, submitted_by_name: user?.name }]);
          setNewUpdateText('');
        } else setError(d.message || 'Failed to add update');
        setAddingUpdate(false);
      })
      .catch(() => { setError('Request failed'); setAddingUpdate(false); });
  };

  const formatUpdateDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (isComplianceFromApi === null) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  // Owner / department-bound view: list + detail with owner-editable form + updates; department-bound can add via modal (no disclosed to CMS)
  if (showLimitedView) {
    const allowedDeptsForSelect = departments.filter((d) => allowedCilDepartmentIds.some((aid) => String(aid) === String(d.id)));
    return (
      <div className="min-h-screen bg-[#f8fafc] p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-lg font-bold text-slate-800 tracking-tight">{isDepartmentBound ? 'Department Issues' : 'My Assigned Issues'}</h1>
              <p className="text-slate-500 text-sm mt-0.5">
                {isDepartmentBound ? 'Add issues for your department; provide root cause, remediation, and updates. Compliance controls status and disclosure to CMS.' : 'Provide root cause, remediation, and updates. Compliance controls status and closure.'}
              </p>
            </div>
            <div className="flex items-center">
              <TabBar value={cilView === 'closed' ? 'closed' : 'log'} onChange={(v) => { setCilView(v); if (v === 'closed') { setFormOpen(false); setEditingId(null); } }}
                tabs={[['log', 'Open issues', openIssues.length], ['closed', 'Closed issues', closedIssues.length]]} />
              {isDepartmentBound && cilView !== 'closed' && (
                <button type="button" onClick={openAddModal} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90">
                  + Add issue
                </button>
              )}
            </div>
          </div>

          <PhiFriendlyNote className="mb-4" />

          {cilView === 'closed' && (
            <ClosedIssues issues={closedIssues} loading={loading} full={false} canReopen={false} />
          )}

          {cilView !== 'closed' && addModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setAddModalOpen(false)}>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Add issue (your department)</h2>
                {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
                <form onSubmit={handleAddModalSubmit} className="space-y-4 text-sm">
                  {allowedDeptsForSelect.length > 1 && (
                    <div>
                      <label className="block text-slate-600 font-medium mb-1">Department(s)</label>
                      <div className="border border-slate-200 rounded-lg p-3 flex flex-wrap gap-2">
                        {allowedDeptsForSelect.map((d) => (
                          <label key={d.id} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(addModalForm.department_ids || []).includes(d.id)}
                              onChange={(e) => {
                                const ids = addModalForm.department_ids || [];
                                const next = e.target.checked ? [...ids, d.id] : ids.filter((id) => id !== d.id);
                                setAddModalForm((f) => ({ ...f, department_ids: next }));
                              }}
                              className="rounded border-slate-300"
                            />
                            <span>{d.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-slate-600 font-medium mb-1">Program area impacted</label>
                    <input type="text" value={addModalForm.program_area_impacted} onChange={(e) => setAddModalForm((f) => ({ ...f, program_area_impacted: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium mb-1">Issue description</label>
                    <textarea value={addModalForm.issue_description} onChange={(e) => setAddModalForm((f) => ({ ...f, issue_description: e.target.value }))} rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium mb-1">Root cause analysis</label>
                    <textarea value={addModalForm.root_cause_analysis} onChange={(e) => setAddModalForm((f) => ({ ...f, root_cause_analysis: e.target.value }))} rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium mb-1">Member impact (Y/N)</label>
                    <select value={addModalForm.member_impact} onChange={(e) => setAddModalForm((f) => ({ ...f, member_impact: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2">
                      <option value="">—</option>
                      <option value="Y">Y</option>
                      <option value="N">N</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium mb-1">Enrollees impacted (if Y)</label>
                    <input type="number" min={0} value={addModalForm.enrollees_impacted} onChange={(e) => setAddModalForm((f) => ({ ...f, enrollees_impacted: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" disabled={addModalForm.member_impact !== 'Y'} />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium mb-1">Date issue identified (YYYY/MM/DD)</label>
                    <input type="text" placeholder="YYYY/MM/DD" value={addModalForm.date_issue_identified} onChange={(e) => setAddModalForm((f) => ({ ...f, date_issue_identified: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium mb-1">System/operational remediation description</label>
                    <textarea value={addModalForm.system_remediation_description} onChange={(e) => setAddModalForm((f) => ({ ...f, system_remediation_description: e.target.value }))} rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-600 font-medium mb-1">Remediation started (YYYY/MM/DD)</label>
                      <input type="text" placeholder="YYYY/MM/DD" value={addModalForm.system_remediation_started_at} onChange={(e) => setAddModalForm((f) => ({ ...f, system_remediation_started_at: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
                    </div>
                    <div>
                      <label className="block text-slate-600 font-medium mb-1">Remediation completed (YYYY/MM/DD)</label>
                      <input type="text" placeholder="YYYY/MM/DD" value={addModalForm.system_remediation_completed_at} onChange={(e) => setAddModalForm((f) => ({ ...f, system_remediation_completed_at: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium mb-1">Source</label>
                    <select value={addModalForm.source} onChange={(e) => setAddModalForm((f) => ({ ...f, source: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2">
                      <option value="">—</option>
                      {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{sourceLabel(s)}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-medium disabled:opacity-50">Add issue</button>
                    <button type="button" onClick={() => { setAddModalOpen(false); setError(''); }} className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50">Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {cilView !== 'closed' && formOpen && editingId ? (() => {
            const editingIssue = issues.find((i) => i.id === editingId);
            const isClosedReadOnly = editingIssue?.status === 'closed';
            return (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-md p-6 mb-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">{isClosedReadOnly ? 'Issue (closed)' : 'Update issue'}</h2>
              {isClosedReadOnly && <p className="text-sm text-slate-500 mb-4">This issue is closed. You can view details only.</p>}
              {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
              <div className="mb-6 p-4 bg-slate-50 rounded-xl">
                <p className="text-sm font-medium text-slate-600">Issue {editingIssue?.id?.slice(0, 8) || '—'}</p>
                <p className="text-slate-700 mt-1">{editingIssue?.program_area_impacted || '—'}</p>
                <p className="text-slate-600 text-sm mt-1">{editingIssue?.issue_description || '—'}</p>
              </div>
              <form onSubmit={handleOwnerSubmit} className="space-y-4 text-sm">
                <div>
                  <label className="block text-slate-600 font-medium mb-1">Root cause analysis</label>
                  <textarea value={ownerForm.root_cause_analysis} onChange={(e) => setOwnerForm((f) => ({ ...f, root_cause_analysis: e.target.value }))} rows={3} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedReadOnly} />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-1">Enrollees impacted</label>
                  <input type="number" min={0} value={ownerForm.enrollees_impacted} onChange={(e) => setOwnerForm((f) => ({ ...f, enrollees_impacted: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedReadOnly} />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-1">System/operational remediation description</label>
                  <textarea value={ownerForm.system_remediation_description} onChange={(e) => setOwnerForm((f) => ({ ...f, system_remediation_description: e.target.value }))} rows={3} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedReadOnly} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-slate-600 font-medium mb-1">Remediation started (YYYY/MM/DD)</label>
                    <input type="text" placeholder="YYYY/MM/DD" value={ownerForm.system_remediation_started_at} onChange={(e) => setOwnerForm((f) => ({ ...f, system_remediation_started_at: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedReadOnly} />
                  </div>
                  <div>
                    <label className="block text-slate-600 font-medium mb-1">Remediation completed (YYYY/MM/DD)</label>
                    <input type="text" placeholder="YYYY/MM/DD" value={ownerForm.system_remediation_completed_at} onChange={(e) => setOwnerForm((f) => ({ ...f, system_remediation_completed_at: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedReadOnly} />
                  </div>
                </div>
                {!isClosedReadOnly && (
                <div className="flex gap-2 pt-2">
                  <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-medium disabled:opacity-50">Save changes</button>
                  <button type="button" onClick={closeForm} className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50">Back to list</button>
                </div>
                )}
                {isClosedReadOnly && (
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={closeForm} className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50">Back to list</button>
                </div>
                )}
              </form>
              <div className="mt-8 pt-6 border-t border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-3">Your updates</h3>
                <div className="space-y-3 mb-4">
                  {ownerUpdates.length === 0 ? (
                    <p className="text-slate-500 text-sm">No updates yet.</p>
                  ) : (
                    ownerUpdates.map((u) => (
                      <div key={u.id} className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">{formatUpdateDate(u.created_at)} — {u.submitted_by_name || 'You'}</p>
                        <p className="text-slate-700 mt-1">{u.update_text}</p>
                      </div>
                    ))
                  )}
                </div>
                {!isClosedReadOnly && (
                <div>
                  <label className="block text-slate-600 font-medium mb-1">Add update</label>
                  <textarea value={newUpdateText} onChange={(e) => setNewUpdateText(e.target.value)} rows={2} placeholder="e.g. Met with IT. Root cause was batch job failure..." className="w-full rounded-lg border border-slate-200 px-3 py-2" />
                  <button type="button" onClick={handleAddOwnerUpdate} disabled={!newUpdateText.trim() || addingUpdate} className="mt-2 px-4 py-2 rounded-lg bg-slate-600 text-white text-sm font-medium disabled:opacity-50">Submit update</button>
                </div>
                )}
              </div>
            </div>
            );
          })() : null}

          {cilView !== 'closed' && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-md overflow-hidden">
            {loading ? (
              <div className="py-12 text-center text-slate-500">Loading...</div>
            ) : openIssues.length === 0 ? (
              <div className="py-12 text-center text-slate-500">{isDepartmentBound ? 'No open issues in your department.' : 'No open issues assigned to you.'}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-slate-700 font-medium">
                      <th className="py-3 px-3">#</th>
                      <th className="py-3 px-3">Program area</th>
                      <th className="py-3 px-3">Department</th>
                      <th className="py-3 px-3">Status</th>
                      <th className="py-3 px-3">Date identified</th>
                      <th className="py-3 px-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {openIssues.map((row) => (
                      <tr key={row.id} className="hover:bg-slate-50">
                        <td className="py-3 px-3 text-slate-600 font-mono text-xs">{row.id ? row.id.slice(0, 8) : '—'}</td>
                        <td className="py-3 px-3 text-slate-600">{row.program_area_impacted || '—'}</td>
                        <td className="py-3 px-3 text-slate-600">{row.department_names || row.department_name || '—'}</td>
                        <td className="py-3 px-3">
                          <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-800">{statusLabel(row.status)}</span>
                        </td>
                        <td className="py-3 px-3 text-slate-600">{row.date_issue_identified ? isoToCmsDate(row.date_issue_identified) : '—'}</td>
                        <td className="py-3 px-3">
                          <button type="button" onClick={() => openOwnerEdit(row)} className="text-blue-600 hover:underline font-medium">View & update</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    );
  }

  // Compliance view: full form + owner updates section
  const cilViewToggle = (
    <TabBar value={cilView} onChange={(v) => { setCilView(v); if (v !== 'log') { setFormOpen(false); setEditingId(null); } }}
      tabs={[['log', 'Open issues', openIssues.length], ['closed', 'Closed issues', closedIssues.length], ['correspondence', 'Correspondence']]} />
  );
  const noticeBar = notice ? (
    <div className="mb-4 flex items-center justify-between p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm">
      <span>{notice}</span>
      <button type="button" onClick={() => setNotice('')} className="text-emerald-700 hover:text-emerald-900 text-xs font-semibold">Dismiss</button>
    </div>
  ) : null;
  if (cilView === 'closed') {
    return (
      <div className="min-h-screen bg-[#f8fafc] p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-lg font-bold text-slate-800 tracking-tight">Compliance Issues Log</h1>
              <p className="text-slate-500 text-sm mt-0.5">The record of every issue Compliance has closed. Reopen one here if it comes back.</p>
            </div>
            <div>{cilViewToggle}</div>
          </div>
          <PhiFriendlyNote className="mb-4" />
          {noticeBar}
          <ClosedIssues issues={closedIssues} loading={loading} full canReopen onReopened={() => loadIssues()} />
        </div>
      </div>
    );
  }
  if (cilView === 'correspondence') {
    return (
      <div className="min-h-screen bg-[#f8fafc] p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-lg font-bold text-slate-800 tracking-tight">Compliance Issues Log</h1>
              <p className="text-slate-500 text-sm mt-0.5">The mail record for the log's issues.</p>
            </div>
            <div>{cilViewToggle}</div>
          </div>
          <HubCorrespondence base="/api/compliance-issues-log/correspondence" chipFor={() => 'Issue'}
            intro="Every email sent from Workstream about a compliance issue, with its follow-ups and replies in one thread. Reply here and it goes out the same way, into the same thread." />
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[#f8fafc] p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-lg font-bold text-slate-800 tracking-tight">Compliance Issues Log</h1>
            <p className="text-slate-500 text-sm mt-0.5">Pre-Audit Issue Summary alignment. Department and accountable owner from onboarding.</p>
          </div>
          <div className="flex items-center">
            {cilViewToggle}
            <button
              type="button"
              onClick={openAdd}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-600 to-amber-500 text-white text-sm font-semibold hover:opacity-90"
            >
              + Add issue
            </button>
          </div>
        </div>

        <PhiFriendlyNote className="mb-4" />
        {noticeBar}

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold uppercase text-slate-500">Filters</span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white min-w-[120px]"
          >
            <option value="">All open issues</option>
            {OPEN_STATUS_FILTERS.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </select>
          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            className="rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white min-w-[160px]"
          >
            <option value="">All departments</option>
            {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>

        {formOpen && (() => {
          const isClosedViewOnly = Boolean(editingId && editingStatus === 'closed');
          return (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">{editingId ? 'Edit issue' : 'Add issue'}</h2>
            {editingId && (
              <details className="mb-5 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
                <summary className="text-sm font-semibold text-slate-700 cursor-pointer select-none">Correspondence — this issue only</summary>
                <div className="mt-3">
                  <HubCorrespondence base="/api/compliance-issues-log/correspondence" item={editingId} chipFor={() => 'Issue'}
                    intro="Every email sent from Workstream about THIS issue, with its follow-ups and replies. Reply here and it goes out the same way, into the same thread." />
                </div>
              </details>
            )}
            {isClosedViewOnly && <p className="text-sm text-slate-500 mb-4">This issue is closed. Set Status to "open" and save to reopen it, then the other fields become editable.</p>}
            {error && <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm">{error}</div>}
            {editingId && (
              <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <h3 className="font-semibold text-amber-800 mb-2">Owner updates</h3>
                {ownerUpdates.length === 0 ? (
                  <p className="text-xs text-amber-700">No owner updates yet.</p>
                ) : (
                  <div className="space-y-2">
                    {ownerUpdates.map((u) => (
                      <div key={u.id} className="text-sm">
                        <span className="text-amber-700 font-medium">{formatUpdateDate(u.created_at)} — {u.submitted_by_name}</span>
                        <p className="text-slate-700 mt-0.5">{u.update_text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <label className="block text-slate-600 font-medium mb-1">Departments * (select one or more)</label>
                <div className="border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                  {departments.map((d) => (
                    <label key={d.id} className={`flex items-center gap-2 py-1 ${isClosedViewOnly ? '' : 'cursor-pointer'}`}>
                      <input
                        type="checkbox"
                        checked={(form.department_ids || []).includes(d.id)}
                        onChange={(e) => {
                          if (isClosedViewOnly) return;
                          const ids = form.department_ids || [];
                          const next = e.target.checked ? [...ids, d.id] : ids.filter((id) => id !== d.id);
                          setForm((f) => ({ ...f, department_ids: next, accountable_owner_ids: [] }));
                        }}
                        className="rounded border-slate-300"
                        disabled={isClosedViewOnly}
                      />
                      <span className="text-sm">{d.name}</span>
                    </label>
                  ))}
                </div>
                {(form.department_ids || []).length === 0 && !isClosedViewOnly && <p className="text-xs text-amber-600 mt-1">Select at least one department</p>}
              </div>
              <div>
                <label className="block text-slate-600 font-medium mb-1">Accountable owners (select one or more)</label>
                <div className="border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                  {usersByDept.length === 0 ? (
                    <p className="text-sm text-slate-500">Select department(s) first</p>
                  ) : (
                    usersByDept.map((u) => (
                      <label key={u.id} className={`flex items-center gap-2 py-1 ${isClosedViewOnly ? '' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={(form.accountable_owner_ids || []).includes(u.id)}
                          onChange={(e) => {
                            if (isClosedViewOnly) return;
                            const ids = form.accountable_owner_ids || [];
                            const next = e.target.checked ? [...ids, u.id] : ids.filter((id) => id !== u.id);
                            setForm((f) => ({ ...f, accountable_owner_ids: next }));
                          }}
                          className="rounded border-slate-300"
                          disabled={isClosedViewOnly}
                        />
                        <span className="text-sm">{u.name}</span>
                        {u.email && <span className="text-xs text-slate-500">({u.email})</span>}
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div>
                <label className="block text-slate-600 font-medium mb-1">Program area impacted</label>
                <input type="text" value={form.program_area_impacted} onChange={(e) => setForm((f) => ({ ...f, program_area_impacted: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedViewOnly} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-slate-600 font-medium mb-1">Issue description</label>
                <textarea value={form.issue_description} onChange={(e) => setForm((f) => ({ ...f, issue_description: e.target.value }))} rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedViewOnly} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-slate-600 font-medium mb-1">Root cause analysis</label>
                <textarea value={form.root_cause_analysis} onChange={(e) => setForm((f) => ({ ...f, root_cause_analysis: e.target.value }))} rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedViewOnly} />
              </div>
              <div>
                <label className="block text-slate-600 font-medium mb-1">Member impact (Y/N)</label>
                <select value={form.member_impact} onChange={(e) => setForm((f) => ({ ...f, member_impact: e.target.value, enrollees_impacted: e.target.value === 'N' ? '' : f.enrollees_impacted }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" disabled={isClosedViewOnly}>
                  <option value="">—</option>
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </div>
              <div>
                <label className="block text-slate-600 font-medium mb-1">Enrollees impacted (if Y)</label>
                <input type="number" min={0} value={form.enrollees_impacted} onChange={(e) => setForm((f) => ({ ...f, enrollees_impacted: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" disabled={form.member_impact !== 'Y' || isClosedViewOnly} readOnly={isClosedViewOnly} />
              </div>
              <div>
                <label className="block text-slate-600 font-medium mb-1">Date issue identified (YYYY/MM/DD)</label>
                <input type="text" placeholder="YYYY/MM/DD" value={form.date_issue_identified} onChange={(e) => setForm((f) => ({ ...f, date_issue_identified: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedViewOnly} />
              </div>
              <div>
                <label className="block text-slate-600 font-medium mb-1">Date disclosed to CMS (YYYY/MM/DD)</label>
                <input type="text" placeholder="YYYY/MM/DD" value={form.date_issue_disclosed_to_cms} onChange={(e) => setForm((f) => ({ ...f, date_issue_disclosed_to_cms: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedViewOnly} />
              </div>
              <div>
                <label className="block text-slate-600 font-medium mb-1">Disclosed to (name)</label>
                <input type="text" value={form.disclosed_to_name} onChange={(e) => setForm((f) => ({ ...f, disclosed_to_name: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedViewOnly} />
              </div>
              <div>
                <label className="block text-slate-600 font-medium mb-1">Fully remediated (Y/N)</label>
                <select value={form.fully_remediated_flag} onChange={(e) => setForm((f) => ({ ...f, fully_remediated_flag: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" disabled={isClosedViewOnly}>
                  <option value="">—</option>
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-slate-600 font-medium mb-1">System/operational remediation description</label>
                <textarea value={form.system_remediation_description} onChange={(e) => setForm((f) => ({ ...f, system_remediation_description: e.target.value }))} rows={2} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedViewOnly} />
              </div>
              <div>
                <label className="block text-slate-600 font-medium mb-1">System remediation started (YYYY/MM/DD)</label>
                <input type="text" placeholder="YYYY/MM/DD" value={form.system_remediation_started_at} onChange={(e) => setForm((f) => ({ ...f, system_remediation_started_at: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedViewOnly} />
              </div>
              <div>
                <label className="block text-slate-600 font-medium mb-1">System remediation completed (YYYY/MM/DD)</label>
                <input type="text" placeholder="YYYY/MM/DD" value={form.system_remediation_completed_at} onChange={(e) => setForm((f) => ({ ...f, system_remediation_completed_at: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" readOnly={isClosedViewOnly} />
              </div>
              <div>
                <label className="block text-slate-600 font-medium mb-1">Status</label>
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2">
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-slate-600 font-medium mb-1">Source</label>
                <select value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" disabled={isClosedViewOnly}>
                  <option value="">—</option>
                  {SOURCE_OPTIONS.map((s) => <option key={s} value={s}>{sourceLabel(s)}</option>)}
                </select>
              </div>
              <div className="md:col-span-2 border-t border-slate-100 pt-4 mt-1">
                <label className="block text-slate-600 font-medium mb-1">Issue pertains to</label>
                <select
                  value={form.entity_kind}
                  onChange={(e) => setForm((f) => ({ ...f, entity_kind: e.target.value, ...(e.target.value === 'internal' ? { fdr_entity_id: '', fdr_contact_email: '' } : {}) }))}
                  className="w-full md:w-1/2 rounded-lg border border-slate-200 px-3 py-2"
                  disabled={isClosedViewOnly}
                >
                  <option value="internal">Internal Health Plan</option>
                  <option value="fdr">FDR</option>
                </select>
                {form.entity_kind === 'fdr' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                      <label className="block text-slate-600 font-medium mb-1">FDR entity *</label>
                      <select
                        value={form.fdr_entity_id}
                        onChange={(e) => setForm((f) => ({ ...f, fdr_entity_id: e.target.value, fdr_contact_email: '' }))}
                        className="w-full rounded-lg border border-slate-200 px-3 py-2"
                        disabled={isClosedViewOnly}
                      >
                        <option value="">Select an FDR…</option>
                        {fdrEntities.map((en) => (
                          <option key={en.id} value={en.id}>{en.name}{en.entity_type ? ` (${String(en.entity_type).replace(/_/g, ' ')})` : ''}</option>
                        ))}
                      </select>
                      {fdrEntities.length === 0 && <p className="text-xs text-slate-400 mt-1">No FDRs are registered in the FDR app yet.</p>}
                    </div>
                    <div>
                      <label className="block text-slate-600 font-medium mb-1">Contact email (select one or enter a new one)</label>
                      <input
                        type="email"
                        list={`fdr-emails-${form.fdr_entity_id || 'none'}`}
                        value={form.fdr_contact_email}
                        onChange={(e) => setForm((f) => ({ ...f, fdr_contact_email: e.target.value }))}
                        placeholder="name@fdr.com"
                        className="w-full rounded-lg border border-slate-200 px-3 py-2"
                        disabled={isClosedViewOnly || !form.fdr_entity_id}
                      />
                      <datalist id={`fdr-emails-${form.fdr_entity_id || 'none'}`}>
                        {(fdrEntities.find((en) => en.id === form.fdr_entity_id)?.emails || []).map((em) => <option key={em} value={em} />)}
                      </datalist>
                    </div>
                  </div>
                )}
              </div>
              <div className="md:col-span-2 flex gap-2 pt-2">
                <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-medium disabled:opacity-50">
                  {saving ? 'Saving...' : (editingId ? (isClosedViewOnly ? 'Reopen / Save' : 'Save changes') : 'Add issue')}
                </button>
                <button type="button" onClick={closeForm} className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-medium hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </form>
          </div>
          );
        })()}

        <div className="bg-white rounded-2xl border border-slate-200 shadow-md overflow-hidden">
          {loading ? (
            <div className="py-12 text-center text-slate-500">Loading...</div>
          ) : openIssues.length === 0 ? (
            <div className="py-12 text-center text-slate-500">{closedIssues.length ? 'Every issue is closed. The record is under Closed issues.' : 'No issues yet. Add one to get started.'}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-slate-700 font-medium">
                    <th className="py-3 px-3">#</th>
                    <th className="py-3 px-3">Program area</th>
                    <th className="py-3 px-3">Type</th>
                    <th className="py-3 px-3">Department</th>
                    <th className="py-3 px-3">Accountable owner</th>
                    <th className="py-3 px-3">Status</th>
                    <th className="py-3 px-3">Date identified</th>
                    <th className="py-3 px-3">Member impact</th>
                    <th className="py-3 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {openIssues.map((row) => (
                    <tr key={row.id} className="hover:bg-slate-50">
                      <td className="py-3 px-3 text-slate-600 font-mono text-xs">{row.id ? row.id.slice(0, 8) : '—'}</td>
                      <td className="py-3 px-3 text-slate-600">{row.program_area_impacted || '—'}</td>
                      <td className="py-3 px-3 text-slate-600">{(row.entity_kind === 'fdr' || row.fdr_entity_id) ? `FDR${row.fdr_entity_name ? ': ' + row.fdr_entity_name : ''}` : 'Internal'}</td>
                      <td className="py-3 px-3 text-slate-600">{row.department_names || row.department_name || '—'}</td>
                      <td className="py-3 px-3 text-slate-600">{row.accountable_owner_names || row.accountable_owner_name || '—'}</td>
                      <td className="py-3 px-3">
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${row.status === 'in_progress' ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}`}>{statusLabel(row.status)}</span>
                      </td>
                      <td className="py-3 px-3 text-slate-600">{row.date_issue_identified ? isoToCmsDate(row.date_issue_identified) : '—'}</td>
                      <td className="py-3 px-3 text-slate-600">{row.member_impact === 'Y' ? `Yes (${row.enrollees_impacted ?? '—'})` : row.member_impact === 'N' ? 'No' : '—'}</td>
                      <td className="py-3 px-3">
                        <button type="button" onClick={() => { openEdit(row); setTimeout(() => loadOwnerUpdates(row.id), 0); }} className="text-blue-600 hover:underline font-medium">Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
